/**
 * Training Service
 * Handles ZIP creation and training job submission
 */

import axios from 'axios';

const TRAIN_UTILS_API_URL = process.env.REACT_APP_TRAIN_UTILS_API_URL;
const TRAIN_UTILS_API_KEY = process.env.REACT_APP_TRAIN_UTILS_API_KEY;
const RUNPOD_API_KEY = process.env.REACT_APP_RUNPOD_API_KEY;
const RUNPOD_TRAINING_ENDPOINT = process.env.REACT_APP_RUNPOD_TRAINING_ENDPOINT;
const S3_BUCKET = process.env.REACT_APP_S3_BUCKET_IMAGES;

class TrainingService {
  /**
   * Create a ZIP file from the cropped folder
   * @param {string} influencerId - The influencer ID
   * @returns {Promise<Object>} ZIP creation response
   */
  static async createDatasetZip(influencerId) {
    if (!influencerId) {
      throw new Error('Influencer ID is required');
    }

    const s3FolderPath = `s3://${S3_BUCKET}/${influencerId}/cropped/`;
    const outputPath = `s3://oh-datasets/${influencerId}_dataset_${Date.now()}.zip`;

    console.log('📦 Creating dataset ZIP:', {
      source: s3FolderPath,
      output: outputPath
    });

    try {
      // Use API key for API Gateway endpoint
      const headers = {
        'X-API-Key': TRAIN_UTILS_API_KEY,
        'Content-Type': 'application/json'
      };
      
      const response = await axios.post(
        `${TRAIN_UTILS_API_URL}api/v1/zip`,
        {
          s3_folder_path: s3FolderPath,
          content_type: 'training', // Only include images and txt files
          output_s3_path: outputPath
        },
        { headers }
      );

      console.log('✅ Dataset ZIP created:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to create dataset ZIP:', error);
      throw new Error(`Failed to create dataset ZIP: ${error.response?.data?.detail || error.message}`);
    }
  }

  /**
   * Submit a training job to RunPod
   * @param {Object} influencer - The influencer object
   * @param {Object} settings - Training settings from the UI
   * @param {string} datasetPath - S3 path to the dataset ZIP
   * @returns {Promise<Object>} Training job response
   */
  static async submitTrainingJob(influencer, settings, datasetPath) {
    if (!influencer || !settings || !datasetPath) {
      throw new Error('Missing required parameters for training job');
    }

    // Convert resolution string to tuple format
    const resolutionParts = settings.resolution.split('x').map(Number);
    const resolution = [resolutionParts[0], resolutionParts[1]];

    // Prepare the training request matching FluxLoRATrainingRequest schema
    // RunPod expects the data nested under input.payload
    const trainingRequest = {
      input: {
        payload: {
          name: settings.name || `${influencer.login}_training_${Date.now()}`,
          dataset: datasetPath,
          checkpoint: settings.checkpoint || 'level_2_v3/', // Use checkpoint directly from settings
          trigger: settings.trigger || influencer.login,
          validation_prompt: settings.validation_prompt || `${influencer.login}, standing on beach`,
          validation_steps: parseInt(settings.validation_steps) || 1000,
          steps: parseInt(settings.steps) || 15000,
          rank: parseInt(settings.rank) || 64,
          seed: null, // Optional, will use random if null
          webhook: null, // Optional webhook for completion notification
          project: settings.project || 'twins',
          resolution: resolution,
          lora_type: settings.lora_type === 'lycoris' ? 'lycoris' : 'standard',
          learning_rate: parseFloat(settings.learning_rate) || 0.0001,
          prepend_instance_prompt: true, // Prepend trigger word to captions
          cid: influencer.id ? parseInt(influencer.id.replace(/-/g, '').substring(0, 8), 16) : null // Convert UUID to int for webhook
        }
      }
    };

    console.log('🚀 Submitting training job:', JSON.stringify(trainingRequest, null, 2));
    console.log('📍 RunPod endpoint:', `https://api.runpod.ai/v2/${RUNPOD_TRAINING_ENDPOINT}/run`);

    try {
      const response = await axios.post(
        `https://api.runpod.ai/v2/${RUNPOD_TRAINING_ENDPOINT}/run`,
        trainingRequest,
        {
          headers: {
            'Authorization': `Bearer ${RUNPOD_API_KEY}`,
            'Content-Type': 'application/json'
          },
          timeout: 10000 // 10 second timeout for async call
        }
      );

      console.log('✅ Training job submitted:', response.data);
      
      // RunPod returns immediately with a job ID
      // The response should contain an id field for tracking
      if (!response.data?.id) {
        console.warn('⚠️ No job ID in response:', response.data);
      }
      
      return response.data;
    } catch (error) {
      console.error('❌ Failed to submit training job:', error);
      throw new Error(`Failed to submit training job: ${error.response?.data?.error || error.message}`);
    }
  }

  /**
   * Start the complete training pipeline
   * @param {Object} influencer - The influencer object
   * @param {Object} settings - Training settings from the UI
   * @returns {Promise<Object>} Combined response with ZIP and training job info
   */
  static async startTrainingPipeline(influencer, settings) {
    try {
      // Step 1: Create dataset ZIP
      console.log('📋 Step 1: Creating dataset ZIP...');
      const zipResponse = await this.createDatasetZip(influencer.id);
      
      if (!zipResponse.output_path) {
        throw new Error('Failed to get ZIP output path');
      }

      // Step 2: Submit training job with the ZIP
      console.log('📋 Step 2: Submitting training job...');
      const trainingResponse = await this.submitTrainingJob(
        influencer, 
        settings, 
        zipResponse.output_path
      );

      return {
        success: true,
        zip: zipResponse,
        training: trainingResponse,
        jobId: trainingResponse.id,
        message: `Training job submitted successfully! Job ID: ${trainingResponse.id || 'Processing'}`
      };
    } catch (error) {
      console.error('❌ Training pipeline failed:', error);
      return {
        success: false,
        error: error.message,
        message: `Failed to start training: ${error.message}`
      };
    }
  }

  /**
   * Check training job status
   * @param {string} jobId - The RunPod job ID
   * @returns {Promise<Object>} Job status response
   */
  static async checkJobStatus(jobId) {
    try {
      const response = await axios.get(
        `https://api.runpod.ai/v2/${RUNPOD_TRAINING_ENDPOINT}/status/${jobId}`,
        {
          headers: {
            'Authorization': `Bearer ${RUNPOD_API_KEY}`
          }
        }
      );

      return response.data;
    } catch (error) {
      console.error('Failed to check job status:', error);
      throw error;
    }
  }
}

export default TrainingService;